import { ProviderDrmDataObject, ProviderMediaConfigSourcesObject } from '@playkit-js/playkit-js-providers/types';

import { isLiveProgram } from '../asset/utils';
import type { RProgram } from '../program/type';

import { DrmData, type PlaybackContext } from './types';

export type CreatePlayerProgramConfigSourcesArgs = {
  program: RProgram;
  playbackContext: PlaybackContext;
};

type Format = {
  name: 'dash' | 'hls';
  mimeType: string;
};

const supportedStreamFormat: Record<string, Format> = {
  mpegdash: {
    name: 'dash',
    mimeType: 'application/dash+xml',
  },
  applehttp: {
    name: 'hls',
    mimeType: 'application/x-mpegURL',
  },
};

const drmSchemeMap: Record<string, string> = {
  'drm.PLAYREADY_CENC': 'com.microsoft.playready',
  'drm.WIDEVINE_CENC': 'com.widevine.alpha',
  'fairplay.FAIRPLAY': 'com.apple.fairplay',
  WIDEVINE_CENC: 'com.widevine.alpha',
  PLAYREADY_CENC: 'com.microsoft.playready',
  FAIRPLAY: 'com.apple.fairplay',
};

export function createPlayerProgramConfigSources({
  program,
  playbackContext,
}: CreatePlayerProgramConfigSourcesArgs): ProviderMediaConfigSourcesObject {
  const isLive = isLiveProgram(program);
  const metadata = {
    contextType: isLive ? 'START_OVER' : 'CATCHUP',
    epgId: program.externalId,
    mediaType: 'epg',
    name: program.title,
  };

  return {
    ...parseSources(playbackContext.sources),
    document: [],
    dvr: isLive,
    image: [],
    type: isLive ? 'Live' : 'Vod',
    metadata,
    progressive: [],
  };
}

function parseDrm(drmData: DrmData): ProviderDrmDataObject {
  return {
    ...drmData,
    scheme: drmSchemeMap[drmData.scheme],
  };
}

function parseSources(sources: PlaybackContext['sources']) {
  const parsedSources: Pick<ProviderMediaConfigSourcesObject, 'dash' | 'hls'> = {
    dash: [],
    hls: [],
  };

  sources.forEach((source) => {
    const format = supportedStreamFormat[source.format];

    if (!format) {
      return;
    }

    parsedSources[format.name].push({
      id: `${source.id},${source.format}`,
      mimetype: format.mimeType,
      url: source.url,
      ...(source.drmData.length ? { drmData: source.drmData.map(parseDrm) } : undefined),
    });
  });

  return parsedSources;
}
